package com.bikompanion.ui.strings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.platform.LocalLocale
import androidx.compose.ui.text.intl.Locale
import androidx.compose.ui.unit.LayoutDirection
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import cafe.adriel.lyricist.LanguageTag
import cafe.adriel.lyricist.Lyricist

interface BikompanionStrings {
    val actionRetry: String
    val actionClose: String
    val actionSubmit: String
    val detailsLabel: String
    val detailsShow: String
    val detailsHide: String
    val actionGetDirections: String
    val actionShareStation: String
    val linkCopied: String
    val locateShow: String
    val locateFollowHeading: String
    val locateStopFollowing: String
    val settingsTitle: String
    val actionBack: String
    val settingsAppearance: String
    val settingsLanguage: String
    val languageSystem: String
    val themeSystem: String
    val themeLight: String
    val themeDark: String
    val settingsNotifications: String
    val stationAlerts: String
    val notificationsOn: String
    val notificationsOff: String
    val notificationsBlocked: String
    val actionManage: String
    val actionTurnOn: String
    val settingsSupport: String
    val sendFeedback: String
    val sendFeedbackDescription: String
    val shareApp: String
    val settingsAbout: String
    val aboutApp: String
    val aboutDevelopedBy: String
    val aboutDataSources: String
    val sourceCityBikesDescription: String
    val sourceGbfsTitle: String
    val sourceGbfsDescription: String
    val sourceOsmTitle: String
    val sourceOsmDescription: String
    val sourceCartoDescription: String
    val aboutLegal: String
    val licensesTitle: String
    val privacyPolicy: String
    val deleteRatings: String
    val deleteRatingsDescription: String
    val licensesLoadFailed: String
    val aboutVersion: (version: String) -> String
    val issueFlatTire: String
    val issueFrontBrake: String
    val issueRearBrake: String
    val issueChain: String
    val issueGears: String
    val issueSeat: String
    val issueLights: String
    val issueLockOrBasket: String
    val issueFrame: String
    val issueMotor: String
    val issueBattery: String
    val issueOther: String
    val networksLoadFailed: String
    val parkingLoadFailed: String
    val parkingLoading: String
    val stationsLoading: String
    val noNetworkHereTitle: String
    val noNetworkHereHint: String
    val installTitle: String
    val installHintIos: String
    val installHintAndroid: String
    val actionInstall: String
    val parkingTypeStands: String
    val parkingTypeShed: String
    val parkingTypeLockers: String
    val parkingTypeBuilding: String
    val parkingTypeOther: String
    val fieldAddress: String
    val fieldCovered: String
    val fieldYes: String
    val fieldNo: String
    val fieldId: String
    val fieldStationId: String
    val fieldCoordinates: String
    val fieldCapacity: String
    val fieldNetwork: String
    val fieldName: String
    val fieldCity: String
    val fieldCountry: String
    val fieldCompany: String
    val fieldLicense: String
    val fieldLicenseUrl: String
    val availabilityBikes: String
    val availabilityElectric: String
    val availabilityEmpty: String
    val bicingNextPickup: (String) -> String
    val rateTitle: String
    val rateFindingNetworks: String
    val rateWhatsWrong: String
    val rateBikeNumber: String
    val rateElectricBike: String
    val rateVoteBad: String
    val rateVoteGood: String
    val rateWhichNetwork: String
    val refreshRateLimited: String
    val refreshFailed: String
    val refreshNextUpdate: (Int) -> String
    val refreshUpdating: String
    val dataNone: String
    val dataAgeJustNow: String
    val dataAgeMinutes: (Int) -> String
    val dataAgeHours: (Int) -> String
    val dataAgeDays: (Int) -> String
    val actionOpenOperator: (String) -> String
    val watchNormal: String
    val watchElectric: String
    val watchInstallNormal: String
    val watchInstallElectric: String
    val watchStopNormal: String
    val watchStopElectric: String
    val actionCheckRateBike: String
    val layerStations: String
    val layerParking: String
    val mapLabelBikes: (Int) -> String
    val mapLabelEbikes: (Int) -> String
    val mapLabelFree: (Int) -> String
    val scoreHeadline: (String, String) -> String
    val scoreNoRatings: String
    val scoreLooksGood: String
    val scoreHasProblems: String
    val scoreMixed: String
    val ratingNetworkRequired: String
    val ratingThanks: (String) -> String
    val ratingCooldown: (Int) -> String
    val ratingQueued: String
    val ratingInvalidBike: String
    val ratingRejected: String
    val notificationFoundTitle: String
    val notificationFoundNormal: (String) -> String
    val notificationFoundElectric: (String) -> String
    val notificationExpiredTitle: String
    val notificationExpiredNormal: (String) -> String
    val notificationExpiredElectric: (String) -> String
    val channelWatchAlertsName: String
    val channelWatchAlertsDescription: String
    val channelBackgroundWatchName: String
    val notificationWatchingTitle: String
    val notificationWatchingText: String
    val scoreFewRatings: (Int) -> String
    val scorePositiveRides: (Int, Int) -> String
}

object Locales {
    const val En = "en"
    const val Es = "es"
    const val Ca = "ca"
    const val Pt = "pt"
    const val Hi = "hi"
    const val Id = "id"
    const val Ja = "ja"
    const val Ru = "ru"
    const val Tr = "tr"
    const val De = "de"
    const val Ar = "ar"
}

val bikompanionStrings: Map<LanguageTag, BikompanionStrings> = mapOf(
    Locales.En to EnBikompanionStrings,
    Locales.Es to EsBikompanionStrings,
    Locales.Ca to CaBikompanionStrings,
    Locales.Pt to PtBikompanionStrings,
    Locales.Hi to HiBikompanionStrings,
    Locales.Id to IdBikompanionStrings,
    Locales.Ja to JaBikompanionStrings,
    Locales.Ru to RuBikompanionStrings,
    Locales.Tr to TrBikompanionStrings,
    Locales.De to DeBikompanionStrings,
    Locales.Ar to ArBikompanionStrings,
)

val LocalStrings: ProvidableCompositionLocal<BikompanionStrings> = staticCompositionLocalOf { EnBikompanionStrings }

@Composable
fun rememberBikompanionStrings(
    defaultLanguageTag: LanguageTag = Locales.En,
    currentLanguageTag: LanguageTag = LocalLocale.current.toLanguageTag(),
): Lyricist<BikompanionStrings> =
    remember(defaultLanguageTag, currentLanguageTag) {
        Lyricist(defaultLanguageTag, bikompanionStrings).apply {
            languageTag = currentLanguageTag
        }
    }

/**
 * Sets the layout direction here rather than leaving it to the host, because the web root is
 * always left-to-right, and because a right-to-left system language with no translation shows
 * English, which should not be mirrored.
 */
@Composable
fun ProvideStrings(
    lyricist: Lyricist<BikompanionStrings> = rememberBikompanionStrings(),
    content: @Composable () -> Unit,
) {
    val state by lyricist.state.collectAsStateWithLifecycle()
    val direction = if (state.strings === ArBikompanionStrings) LayoutDirection.Rtl else LayoutDirection.Ltr
    CompositionLocalProvider(
        LocalStrings provides state.strings,
        LocalLayoutDirection provides direction,
        content = content,
    )
}

/**
 * The language the rider picked, or null to follow the device. Composition gets it through
 * `AppTheme`; this is for [getLocaleStrings], which runs where no composition can reach it.
 */
var preferredLanguageTag: String? = null

/**
 * For code that runs outside composition, such as notifications from a background service.
 *
 * Uses the deprecated `Locale.current` because `LocalLocale` needs a composition, and what it gives
 * up is observing a change, which a one-off notification has no use for.
 */
@Suppress("DEPRECATION")
fun getLocaleStrings(locale: Locale = preferredLanguageTag?.let(::Locale) ?: Locale.current): BikompanionStrings {
    val tag = locale.toLanguageTag()
    return bikompanionStrings[tag]
        ?: bikompanionStrings[tag.substringBefore('-')]
        ?: EnBikompanionStrings
}
